import { Check, ChevronDown, Phone, Search, Unlink } from "lucide-react";
import * as React from "react";
import { Badge, StatusDot } from "@/components/ui/badge";
import { EmptyState, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { PICKER_PAGE_SIZE, useEndpointPages } from "@/lib/queries";
import type { Endpoint } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * Choosing one endpoint out of however many exist.
 *
 * The list this replaced fetched everything and rendered it. That is fine at a dozen endpoints
 * and wrong at a hundred: `GET /endpoints` caps at 100 rows, so the hundred-and-first simply
 * was not on screen, with nothing to say so — an endpoint that exists, is spelled correctly,
 * and cannot be picked. Everything here follows from not doing that:
 *
 * - **The server pages, and says how many rows there are.** The footer states the total, so a
 *   partial list is visibly partial. `total` may be null (the header did not survive CORS);
 *   the footer then says how many are loaded and claims nothing about the rest.
 * - **The server searches**, over extension *and* the owner's name and email. People look for
 *   "Sarah", not for 1004, and a picker offering "Rep's room" has to answer that.
 * - **The value is an endpoint, not an id.** Search re-fetches, so the chosen row is often not
 *   in the current results; holding only an id meant the selection silently vanished from the
 *   screen while still being what got saved. Parents seed this from the columns they already
 *   carry (`did.sip_endpoint_extension`, `user.sip_endpoint_extension`), so a picker opens
 *   showing the current choice without fetching anything.
 * - **It collapses.** Open, it is a search box over a scrolling list; closed, it is one line
 *   saying what is chosen — which is what the rest of a form looks like.
 *
 * It expands in place rather than opening a popover: `DialogBody` scrolls, so anything
 * absolutely positioned inside it is clipped at the fold, and a portalled popover then has to
 * be reconciled with the dialog's focus trap. Pushing the form down costs nothing here.
 */

/** The little of an endpoint a picker needs to render a choice. `Endpoint` satisfies it, and
 *  so do the denormalised columns on a `Did` or an `AppUser` — which is the point: the current
 *  selection is displayable before any list has loaded. */
export interface EndpointRef {
  id: number;
  extension: string;
  domain_name: string | null;
  /** Whose phone it is. Null for an endpoint nobody holds. */
  user_name: string | null;
}

export function endpointRef(e: Endpoint): EndpointRef {
  return {
    id: e.id,
    extension: e.extension,
    domain_name: e.domain_name,
    user_name: e.assigned_user_name,
  };
}

/**
 * What the picker offers. One variant today: endpoints somebody holds — routing a number to a
 * room nobody opens is a dead end, so the route dialog never offers an unowned one. Kept as a
 * union because the filter it selects is a real choice, not an implementation detail.
 */
type Mode = { kind: "assigned" };

export function EndpointPicker({
  value, onChange, mode, domainId, allowNone = false, id,
  "aria-describedby": describedBy,
}: {
  value: EndpointRef | null;
  onChange: (value: EndpointRef | null) => void;
  mode: Mode;
  domainId?: number;
  /** Offer "No phone" as a choice, for a field that can be cleared. */
  allowNone?: boolean;
  /** `id` and `aria-describedby` are injected by `<Field>`, which clones its child. */
  id?: string;
  "aria-describedby"?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [active, setActive] = React.useState(0);
  const debounced = useDebounced(search, 200);
  const listId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const wasOpen = React.useRef(false);

  const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useEndpointPages(
    {
      q: debounced || undefined,
      domain_id: domainId,
      assigned: mode.kind === "assigned" ? true : undefined,
    },
    { enabled: open },
  );

  const loaded = React.useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const total = data?.pages.at(-1)?.total ?? null;

  // The chosen endpoint pinned to the top when the current results do not contain it — which is
  // the normal case as soon as anyone types. Without this the list shows nothing selected while
  // a selection is very much in effect.
  const pinned = value && !loaded.some((e) => e.id === value.id) ? value : null;

  const rows: Array<{ key: string; ref: EndpointRef | null; endpoint?: Endpoint; pinned?: true }> = [
    ...(allowNone ? [{ key: "none", ref: null }] : []),
    ...(pinned ? [{ key: `pin-${pinned.id}`, ref: pinned, pinned: true as const }] : []),
    ...loaded.map((e) => ({ key: String(e.id), ref: endpointRef(e), endpoint: e })),
  ];
  // "No phone" is a row but not an endpoint, so it must not stand in for one: with it counted,
  // the assign dialog could never say there was nothing to assign.
  const noEndpoints = loaded.length === 0 && pinned === null;

  React.useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, rows.findIndex((r) => (r.ref?.id ?? null) === (value?.id ?? null))));
    // Only when the panel opens: re-running this on every keystroke would fight the arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A new result set makes the old cursor meaningless — put it on the first row rather than
  // leaving it past the end of a shorter list.
  React.useEffect(() => {
    setActive(0);
  }, [debounced]);

  React.useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active, loaded.length]);

  // Collapsing unmounts the input, so focus has to be handed back deliberately — otherwise it
  // falls to the body and the next Tab restarts at the top of the dialog.
  React.useEffect(() => {
    if (wasOpen.current && !open) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  const choose = (ref: EndpointRef | null) => {
    onChange(ref);
    setOpen(false);
    setSearch("");
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length === 0) return;
      const next = e.key === "ArrowDown" ? active + 1 : active - 1;
      setActive((next + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (row) choose(row.ref);
    } else if (e.key === "Escape") {
      // Stop here, or Radix closes the whole dialog and the form is gone.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-describedby={describedBy}
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
        className={cn(
          "flex h-auto w-full items-center gap-2.5 rounded-md border border-border bg-surface",
          "px-3 py-2 text-left shadow-xs transition-colors",
          "hover:bg-surface-hover focus-visible:border-accent-8 focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-accent-8/25",
        )}
      >
        {value ? (
          <>
            <Phone className="size-4 shrink-0 text-text-subtle" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block font-mono text-sm text-text">
                {value.extension}
                <span className="text-text-subtle">@{value.domain_name}</span>
              </span>
              <span className="block truncate text-xs text-text-muted">
                {value.user_name ?? "Unassigned"}
              </span>
            </span>
          </>
        ) : (
          <>
            <Unlink className="size-4 shrink-0 text-text-subtle" aria-hidden />
            <span className="flex-1 text-sm text-text-subtle">
              {allowNone ? "No phone" : "Choose an endpoint"}
            </span>
          </>
        )}
        <ChevronDown className="size-4 shrink-0 text-text-subtle" aria-hidden />
      </button>
    );
  }

  return (
    <div
      data-endpoint-picker
      data-closes-on-escape
      className="space-y-2 rounded-md border border-accent-8 bg-surface p-2 ring-2 ring-accent-8/25"
    >
      <Input
        ref={inputRef}
        id={id}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          // Only when focus leaves the picker entirely — clicking a row moves focus within it.
          if (!e.currentTarget.closest("[data-endpoint-picker]")?.contains(e.relatedTarget)) {
            setOpen(false);
          }
        }}
        placeholder="Search by extension or name"
        leading={<Search />}
        role="combobox"
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          !noEndpoints && rows[active] ? `${listId}-${rows[active].key}` : undefined
        }
        aria-describedby={describedBy}
        aria-label="Search endpoints"
      />

      {isPending ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : noEndpoints ? (
        <EmptyState
          icon={Phone}
          title={debounced ? "Nothing matches" : "No endpoints"}
          description={
            debounced
              ? `No endpoint matches "${debounced}" — search runs over extensions and the name on the account.`
              : mode.kind === "assigned"
                ? "Only endpoints assigned to a user can be picked here. Assign one from the Users screen first."
                : "Create an endpoint in this domain first, then come back."
          }
          className="py-6"
        />
      ) : (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Endpoints"
          className="max-h-56 space-y-1 overflow-y-auto scrollbar-subtle"
        >
          {rows.map((row, i) => (
            <li key={row.key}>
              <button
                type="button"
                id={`${listId}-${row.key}`}
                role="option"
                data-index={i}
                aria-selected={(row.ref?.id ?? null) === (value?.id ?? null)}
                tabIndex={-1}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(row.ref)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left",
                  "transition-colors focus:outline-none",
                  (row.ref?.id ?? null) === (value?.id ?? null)
                    ? "border-accent-8 bg-accent-2"
                    : "border-transparent",
                  i === active && (row.ref?.id ?? null) !== (value?.id ?? null) && "bg-surface-hover",
                )}
              >
                {row.ref === null ? (
                  <>
                    <Unlink className="size-4 shrink-0 text-text-subtle" aria-hidden />
                    <span className="flex-1 text-sm">No phone</span>
                  </>
                ) : (
                  <>
                    <StatusDot state={row.endpoint?.registered ?? null} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-sm text-text">
                        {row.ref.extension}
                        <span className="text-text-subtle">@{row.ref.domain_name}</span>
                      </span>
                      <span className="block truncate text-xs text-text-muted">
                        {row.ref.user_name ?? "Unassigned"}
                      </span>
                    </span>
                  </>
                )}
                {row.pinned && (
                  <Badge tone="outline" size="sm">
                    Current
                  </Badge>
                )}
                {row.endpoint && !row.endpoint.enabled && (
                  <Badge tone="warning" size="sm">
                    Disabled
                  </Badge>
                )}
                {(row.ref?.id ?? null) === (value?.id ?? null) && (
                  <Check className="size-4 shrink-0 text-accent-11" aria-hidden />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-2 px-1">
        <p className="text-xs text-text-subtle" aria-live="polite">
          {total === null
            ? `${loaded.length} loaded`
            : `Showing ${loaded.length} of ${total}${debounced ? " matching" : ""}`}
        </p>
        {hasNextPage && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-xs text-accent-11 hover:underline disabled:opacity-50"
          >
            {isFetchingNextPage ? "Loading…" : `Load ${PICKER_PAGE_SIZE} more`}
          </button>
        )}
      </div>
    </div>
  );
}
